import React, { useEffect, useState, useRef, useCallback } from "react";
import { Upload, Search, Trash2, Edit3, Image as ImageIcon, Copy, Check, CheckSquare, Square } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { uploadFilesInBatches } from "@/lib/uploadQueue";

const formatBytes = (b) => {
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
};

export default function MediaLibraryAdmin() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [copied, setCopied] = useState(null);
  const [selected, setSelected] = useState(new Set()); // ids
  const [queue, setQueue] = useState([]);
  const dropRef = useRef(null);
  const fileRef = useRef(null);

  const load = useCallback(async (query = "") => {
    try {
      const r = await api.get("/admin/media", { params: { q: query, limit: 200 } });
      setItems(r.data.items);
      setTotal(r.data.total);
    } catch { toast.error("Could not load media library"); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setTimeout(() => load(q), 250);
    return () => clearTimeout(t);
  }, [q, load]);

  const handleFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    setQueue(files.map((f, i) => ({ id: i, name: f.name, pct: 0, status: "pending" })));
    const { ok, fail } = await uploadFilesInBatches({
      files, concurrency: 3, maxRetries: 2,
      onProgress: (id, pct) => setQueue(prev => prev.map(q2 => q2.id === id ? { ...q2, pct, status: pct >= 100 ? "done" : "uploading" } : q2)),
      onItemDone: (id) => { setQueue(prev => prev.map(q2 => q2.id === id ? { ...q2, pct: 100, status: "done" } : q2)); load(q); },
      onItemFail: (id, err) => setQueue(prev => prev.map(q2 => q2.id === id ? { ...q2, status: "error", error: err?.userMessage || err?.response?.data?.detail || err?.message || "Failed" } : q2)),
    });
    setUploading(false);
    if (ok) toast.success(`Uploaded ${ok}`);
    if (fail) toast.error(`${fail} failed`);
    if (!fail) setTimeout(() => setQueue([]), 1500);
  };

  useEffect(() => {
    const el = dropRef.current;
    if (!el) return;
    const prevent = (e) => { e.preventDefault(); e.stopPropagation(); };
    const onDrop = (e) => {
      prevent(e);
      el.classList.remove("ring-2", "ring-[#D4AF37]");
      handleFiles(Array.from(e.dataTransfer?.files || []).filter(f => f.type.startsWith("image/")));
    };
    const onOver = (e) => { prevent(e); el.classList.add("ring-2", "ring-[#D4AF37]"); };
    const onLeave = (e) => { prevent(e); el.classList.remove("ring-2", "ring-[#D4AF37]"); };
    el.addEventListener("dragover", onOver);
    el.addEventListener("dragleave", onLeave);
    el.addEventListener("drop", onDrop);
    return () => {
      el.removeEventListener("dragover", onOver);
      el.removeEventListener("dragleave", onLeave);
      el.removeEventListener("drop", onDrop);
    };
  }, []); // eslint-disable-line

  const remove = async (m) => {
    if (!window.confirm(`Delete "${m.title || m.original_filename}"? Existing posts using this image will break unless updated.`)) return;
    try {
      await api.delete(`/admin/media/${m.id}`);
      toast.success("Deleted");
      load(q);
    } catch { toast.error("Could not delete"); }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAll = () => setSelected(new Set(items.map(m => m.id)));
  const deselectAll = () => setSelected(new Set());
  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} image${ids.length === 1 ? "" : "s"}? Existing posts using these images will break unless updated.`)) return;
    try {
      const r = await api.post("/admin/media/bulk-delete", { ids });
      toast.success(`Deleted ${r.data.deleted}`);
      setSelected(new Set());
      load(q);
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk delete failed"); }
  };

  const saveEdit = async () => {
    try {
      await api.put(`/admin/media/${editing.id}`, { title: editing.title, alt: editing.alt });
      toast.success("Saved");
      setEditing(null);
      load(q);
    } catch { toast.error("Save failed"); }
  };

  const copyUrl = (m) => {
    const fullUrl = m.urls?.large || m.url;
    navigator.clipboard?.writeText(fullUrl);
    setCopied(m.id);
    toast.success("URL copied");
    setTimeout(() => setCopied(null), 1500);
  };

  return (
    <div data-testid="media-library-admin">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            type="text" placeholder="Search by title, alt, or filename..."
            value={q} onChange={e => setQ(e.target.value)} data-testid="library-search"
            className="w-full bg-black border border-white/15 pl-9 pr-3 py-2.5 text-sm focus:border-[#D4AF37] outline-none" />
        </div>
        <input type="file" multiple accept="image/*" ref={fileRef}
          onChange={e => handleFiles(Array.from(e.target.files || []))} className="hidden" data-testid="library-file-input" />
        <button onClick={() => fileRef.current?.click()} disabled={uploading} className="btn-gold" data-testid="library-upload-btn">
          <Upload size={14}/> {uploading ? "Uploading..." : "Upload Images"}
        </button>
      </div>

      {/* Bulk action bar (visible when items selected) */}
      {items.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 mb-4 text-xs" data-testid="bulk-bar">
          <button onClick={selectAll} className="btn-ghost" data-testid="select-all">
            <CheckSquare size={12}/> Select All ({items.length})
          </button>
          {selected.size > 0 && (
            <>
              <button onClick={deselectAll} className="btn-ghost" data-testid="deselect-all">
                <Square size={12}/> Deselect ({selected.size})
              </button>
              <button onClick={bulkDelete} className="btn-ghost text-red-300 hover:text-red-500 border-red-500/40" data-testid="bulk-delete">
                <Trash2 size={12}/> Delete {selected.size} selected
              </button>
            </>
          )}
        </div>
      )}

      {/* Upload queue with progress */}
      {queue.length > 0 && (
        <div className="mb-4 bg-[#0a0a0a] border border-white/10 p-3 space-y-2 max-h-48 overflow-y-auto" data-testid="library-upload-queue">
          {queue.map(q2 => (
            <div key={q2.id} className="text-xs">
              <div className="flex justify-between gap-3 mb-1">
                <span className="truncate text-white/80" title={q2.name}>{q2.name}</span>
                <span className={`shrink-0 ${q2.status === "error" ? "text-red-400" : "text-white/40"}`}>
                  {q2.status === "error" ? "Failed" : q2.status === "done" ? "Done" : `${q2.pct}%`}
                </span>
              </div>
              <div className="h-1 bg-white/10 overflow-hidden">
                <div className={`h-full transition-all ${q2.status === "error" ? "bg-red-500" : "bg-[#D4AF37]"}`}
                     style={{ width: `${q2.status === "error" ? 100 : q2.pct}%` }} />
              </div>
              {q2.status === "error" && q2.error && <div className="text-[0.65rem] text-red-400/80 mt-1">{q2.error}</div>}
            </div>
          ))}
        </div>
      )}

      <div ref={dropRef}
        className="border-2 border-dashed border-white/10 p-6 transition rounded-none min-h-[60vh]"
        data-testid="library-dropzone">
        {items.length === 0 ? (
          <div className="text-center text-white/40 py-24">
            <ImageIcon size={64} className="mx-auto mb-6 opacity-30"/>
            <p className="text-lg">{q ? "No images match your search." : "Your media library is empty."}</p>
            <p className="text-sm mt-2">Drag &amp; drop images here, or click <strong>Upload Images</strong> above.</p>
          </div>
        ) : (
          <>
            <div className="text-xs text-white/40 mb-4">
              {total} image{total === 1 ? "" : "s"} · Drag &amp; drop more anywhere on this page
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
              {items.map(m => {
                const isSel = selected.has(m.id);
                return (
                <div key={m.id} className={`bg-black border ${isSel ? "border-[#D4AF37] ring-1 ring-[#D4AF37]" : "border-white/10 hover:border-white/30"} group transition relative`} data-testid={`library-item-${m.id}`}>
                  <button onClick={() => toggleSelect(m.id)} title={isSel ? "Deselect" : "Select"}
                    className={`absolute top-2 left-2 z-10 p-1.5 transition ${isSel ? "bg-[#D4AF37] text-black" : "bg-black/70 text-white/70 hover:bg-[#D4AF37] hover:text-black opacity-0 group-hover:opacity-100"}`}
                    data-testid={`select-${m.id}`}>
                    {isSel ? <CheckSquare size={12}/> : <Square size={12}/>}
                  </button>
                  <div className="aspect-square overflow-hidden bg-[#0a0a0a] relative">
                    <img src={m.thumb_url} alt={m.alt || ""} loading="lazy" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-1.5">
                      <button onClick={() => copyUrl(m)} title="Copy URL"
                        className="p-2 bg-black/70 hover:bg-[#D4AF37] hover:text-black" data-testid={`copy-url-${m.id}`}>
                        {copied === m.id ? <Check size={12}/> : <Copy size={12}/>}
                      </button>
                      <button onClick={() => setEditing({ ...m })} title="Edit"
                        className="p-2 bg-black/70 hover:bg-[#D4AF37] hover:text-black" data-testid={`edit-media-${m.id}`}>
                        <Edit3 size={12}/>
                      </button>
                      <button onClick={() => remove(m)} title="Delete"
                        className="p-2 bg-black/70 hover:bg-red-500" data-testid={`delete-media-${m.id}`}>
                        <Trash2 size={12}/>
                      </button>
                    </div>
                  </div>
                  <div className="p-2">
                    <div className="text-xs text-white/80 truncate">{m.title || m.original_filename}</div>
                    <div className="text-[0.6rem] text-white/35 mt-0.5">{formatBytes(m.sizes_bytes?.large || m.sizes_bytes?.medium)} · WebP</div>
                  </div>
                </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-[#0a0a0a] border border-white/15 max-w-lg w-full p-6" onClick={e => e.stopPropagation()}>
            <h4 className="font-serif text-2xl mb-5">Edit Image</h4>
            <img src={editing.thumb_url} alt="" className="w-full aspect-video object-cover mb-5" />
            <label className="block mb-4">
              <span className="text-[0.6rem] tracking-[0.25em] uppercase text-white/60">Title</span>
              <input value={editing.title || ""} onChange={e => setEditing({...editing, title: e.target.value})}
                className="mt-1 w-full bg-black border border-white/15 px-3 py-2 text-sm focus:border-[#D4AF37] outline-none" />
            </label>
            <label className="block mb-5">
              <span className="text-[0.6rem] tracking-[0.25em] uppercase text-white/60">Alt Text (SEO &amp; accessibility)</span>
              <input value={editing.alt || ""} onChange={e => setEditing({...editing, alt: e.target.value})}
                placeholder="Bride walking down the aisle at Casa Loma"
                className="mt-1 w-full bg-black border border-white/15 px-3 py-2 text-sm focus:border-[#D4AF37] outline-none" />
            </label>
            <div className="text-[0.6rem] uppercase tracking-widest text-white/40 mb-2">Available sizes</div>
            <div className="space-y-1 text-xs text-white/60 mb-5">
              {Object.entries(editing.urls || {}).map(([k, u]) => (
                <div key={k} className="flex items-center justify-between gap-2 bg-black px-2 py-1.5 border border-white/10">
                  <span className="text-[#D4AF37] uppercase text-[0.55rem] tracking-widest w-16">{k}</span>
                  <span className="truncate font-mono flex-1">{u}</span>
                  <button onClick={() => { navigator.clipboard?.writeText(u); toast.success("Copied"); }}
                    className="p-1 text-white/50 hover:text-[#D4AF37]"><Copy size={10}/></button>
                </div>
              ))}
            </div>
            <div className="flex gap-3">
              <button onClick={saveEdit} className="btn-gold" data-testid="save-media-edit">Save</button>
              <button onClick={() => setEditing(null)} className="btn-ghost">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
